import { useEffect, useRef, useState } from 'react';
import { getFolderSyncState, putFolderSyncState, deleteFolderSyncState } from './db';
import { webFileSystemAccessProvider, ScanCancelledError } from './provider';
import { nativeFolderSyncProvider, isNativeSyncEnvironment } from './nativeProvider';

// Same native-vs-browser provider selection useLocalAutoSync.js already uses
// for the auto-sync engine - this hook backs the separate, one-off "Choose
// folder" manual picker (SmartSortSection/AddPhotosSection), which was never
// updated to use the SAF-backed native provider and so silently fell back to
// a plain <input webkitdirectory> on native - a folder-picker UI Android's
// WebView doesn't actually support (unlike a real installed Chrome tab), so
// the whole "upload folder" affordance did nothing there. Reusing the
// already-working native provider (no new native code) fixes it.
const provider = isNativeSyncEnvironment() ? nativeFolderSyncProvider : webFileSystemAccessProvider;

// Bounds how many getFile() calls run concurrently while resolving a folder's
// files after listImageFiles has found them (browser/FSA path only - see
// resolveFiles's own doc comment for why native never takes this path at
// all). The old code fired every getFile() in one unbounded Promise.all -
// for a large folder (thousands of photos) that meant thousands of
// simultaneous calls with no way to report progress. Chunking gives real
// incremental progress and keeps the browser's own I/O from being swamped
// at once.
const RESOLVE_CONCURRENCY = 24;

// Resolves real File objects for already-discovered entries via getFile(),
// reporting `onProgress({ phase: 'reading', completed, total })` as each one
// finishes and honoring `signal` for cancellation.
//
// Browser/File-System-Access-API path ONLY. On native, this used to be the
// exact same code that made a big folder pick both slow (getFile() on native
// round-trips through the SAF bridge: copy to app cache, read into a JS Blob,
// delete the cache copy - see nativeFolderSyncProvider.resolveFile) and a
// real storage/memory hazard (materialising thousands of full-resolution
// Blobs at once, right after folder selection, well before upload even
// starts - confirmed on-device as a large app-storage spike, the same
// "eager batch resolution" problem useLocalAutoSync.js's own resolveAndSubmit
// comment already documents and avoids for auto-sync). Native instead builds
// lightweight metadata-only placeholders (see scanFolder) and defers to
// CmUploadWorker to resolve real bytes itself, lazily, straight from SAF via
// the picked folder's treeUri - no bridge round-trip through JS at all. This
// function is only reachable when `provider` is the browser FSA provider,
// where getFile() is a cheap local call and a real File is unavoidable
// anyway (FormData-based upload has no native worker to defer to).
async function resolveFiles(entries, { onProgress, signal } = {}) {
  const total = entries.length;
  const files = new Array(total);
  let completed = 0;
  let cursor = 0;
  onProgress?.({ phase: 'reading', completed: 0, total });
  async function worker() {
    for (;;) {
      if (signal?.aborted) throw new ScanCancelledError();
      const i = cursor;
      cursor += 1;
      if (i >= total) return;
      const file = await entries[i].getFile();
      // Polyfill webkitRelativePath so this drop-in replaces <input webkitdirectory>
      // without touching any of the existing relativePath-derivation code that
      // already reads `file.webkitRelativePath || file.name` everywhere.
      try { file.webkitRelativePath = entries[i].relativePath; } catch { /* non-configurable in some engines - ignore */ }
      files[i] = file;
      completed += 1;
      onProgress?.({ phase: 'reading', completed, total });
    }
  }
  await Promise.all(Array.from({ length: Math.min(RESOLVE_CONCURRENCY, total) }, worker));
  return files;
}

// Lightweight File-shaped placeholders for native - carry only what the rest
// of this codebase actually reads off a locally-picked file before upload
// (isCompressibleEventImage's name/type check, and webkitRelativePath for
// building the upload's relativePath) without ever touching the real bytes.
// Deliberately NOT a real File/Blob - nothing downstream does an instanceof
// check, and the whole point is to avoid materialising one.
function toPlaceholderFile(entry) {
  const relativePath = entry.relativePath;
  const name = relativePath.split('/').pop() || relativePath;
  return { name, webkitRelativePath: relativePath, size: 0, lastModified: Date.now(), type: '' };
}

// Persists the manually-picked "local folder" (non-auto-sync) as a FileSystemDirectoryHandle
// so a page refresh - e.g. after a failed upload - can silently re-hydrate the same file list
// instead of forcing the user back through the OS folder picker. Falls back to the plain
// <input webkitdirectory> flow entirely (via `supported`) on browsers without the File
// System Access API.
export function useManualFolderPersistence({ eventId, onFilesReady }) {
  const [reconnect, setReconnect] = useState(null); // null | { folderName }
  // True while a folder listing (initial pick, the mount-time reattach of a
  // previously-picked folder, or a post-reconnect re-read) is running - a
  // folder with thousands of photos can take a long time end-to-end (listing
  // is fast, but resolveFiles's getFile() calls are not), so the UI needs
  // real feedback plus a way to bail out instead of looking frozen. Mirrors
  // useLocalAutoSync's own scanning/scanProgress/scanCancelRequested/cancelScan
  // shape so callers can share the same progress-bar component.
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [scanCancelRequested, setScanCancelRequested] = useState(false);
  const dirHandleRef = useRef(null);
  const scanAbortRef = useRef(null);
  const onFilesReadyRef = useRef(onFilesReady);
  onFilesReadyRef.current = onFilesReady;

  // Runs listImageFiles + (browser only) resolveFiles as one cancellable,
  // progress-reporting scan - shared by the mount reattach, pickFolder, and
  // reconnectNow, since all three ultimately do the same "known dirHandle ->
  // file list" work, just triggered differently. Throws ScanCancelledError
  // if cancelled. On native, skips resolveFiles entirely (see
  // toPlaceholderFile's doc comment) - the walk itself (listImageFiles) is
  // all that's needed, so scanning finishes as soon as that does.
  const scanFolder = async (handle) => {
    setScanning(true);
    setScanProgress({ phase: 'scanning', completed: 0, total: null });
    setScanCancelRequested(false);
    const controller = new AbortController();
    scanAbortRef.current = controller;
    try {
      const entries = await provider.listImageFiles(handle, {
        onProgress: (p) => setScanProgress(p),
        signal: controller.signal,
      });
      if (isNativeSyncEnvironment()) {
        return entries.map(toPlaceholderFile);
      }
      return await resolveFiles(entries, {
        onProgress: (p) => setScanProgress(p),
        signal: controller.signal,
      });
    } finally {
      scanAbortRef.current = null;
      setScanning(false);
      setScanProgress(null);
      setScanCancelRequested(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!provider.isSupported()) return;
      const rec = await getFolderSyncState(eventId).catch(() => null);
      if (cancelled || !rec || rec.mode !== 'manual' || !rec.dirHandle) return;
      dirHandleRef.current = rec.dirHandle;
      const folderName = rec.dirHandle.name || 'folder';
      const perm = await provider.queryPermission(rec.dirHandle);
      if (cancelled) return;
      if (perm === 'granted') {
        try {
          const files = await scanFolder(rec.dirHandle);
          if (!cancelled) onFilesReadyRef.current(files, folderName, rec.dirHandle.treeUri || null);
        } catch (err) {
          // Cancelled mid-reattach: just leave the file list empty rather than
          // re-throwing into this effect - the user can re-pick if they want.
          if (!(err instanceof ScanCancelledError)) throw err;
        }
      } else {
        setReconnect({ folderName });
      }
    })();
    return () => { cancelled = true; scanAbortRef.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const pickFolder = async () => {
    if (scanAbortRef.current) return null; // a scan is already in flight
    const handle = await provider.pickDirectory();
    if (!handle) return null; // user cancelled the OS picker
    dirHandleRef.current = handle;
    setReconnect(null);
    await putFolderSyncState({ eventId, mode: 'manual', dirHandle: handle, armed: true, batchSizeSetting: 0, seenKeys: [], pendingQueue: [] });
    try {
      const files = await scanFolder(handle);
      return { files, folderName: handle.name || 'folder', treeUri: handle.treeUri || null };
    } catch (err) {
      if (err instanceof ScanCancelledError) return null;
      throw err;
    }
  };

  const reconnectNow = async () => {
    if (!dirHandleRef.current || scanAbortRef.current) return;
    const perm = await provider.requestPermission(dirHandleRef.current);
    if (perm === 'granted') {
      setReconnect(null);
      try {
        const files = await scanFolder(dirHandleRef.current);
        onFilesReadyRef.current(files, dirHandleRef.current.name || 'folder', dirHandleRef.current.treeUri || null);
      } catch (err) {
        if (!(err instanceof ScanCancelledError)) throw err;
      }
    }
  };

  // Aborts an in-flight scan (initial pick, mount reattach, or reconnect) -
  // asynchronous, same as useLocalAutoSync's cancelScan: this only flags the
  // request, the scan itself notices at its next chunk/entry checkpoint.
  const cancelScan = () => {
    if (!scanAbortRef.current) return;
    setScanCancelRequested(true);
    scanAbortRef.current.abort();
  };

  // Escape hatch for a "Continue with X?" reconnect prompt that reconnectNow
  // itself can't clear (SAF/native has no live re-prompt for a lost grant the
  // way the File System Access API does - see nativeProvider.js's own doc
  // comment - so a native permission loss leaves reconnectNow permanently
  // unable to succeed). Forgets this event's persisted folder record entirely
  // rather than just dismissing the prompt in memory, so a page reload
  // doesn't just re-derive the same stuck "Continue with X?" state from
  // IndexedDB - the user picks a fresh folder via the normal "Choose folder"
  // control afterwards.
  const reset = async () => {
    scanAbortRef.current?.abort();
    dirHandleRef.current = null;
    setReconnect(null);
    await deleteFolderSyncState(eventId);
  };

  return {
    supported: provider.isSupported(), reconnect, pickFolder, reconnectNow, reset,
    scanning, scanProgress, scanCancelRequested, cancelScan,
  };
}
